import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';

function clamp(value) {
  var min = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  var max = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 1;
  return Math.min(Math.max(min, value), max);
}
function hexToRgb(color) {
  var colorWithoutHex = color.substr(1);
  var re = new RegExp(".{1,".concat(colorWithoutHex.length >= 6 ? 2 : 1, "}"), 'g');
  var colors = colorWithoutHex.match(re);
  if (colors && colors[0].length === 1) {
    colors = colors.map(function (n) {
      return n + n;
    });
  }
  return colors ? "rgb".concat(colors.length === 4 ? 'a' : '', "(").concat(colors.map(function (n, index) {
    return index < 3 ? parseInt(n, 16) : Math.round(parseInt(n, 16) / 255 * 1000) / 1000;
  }).join(', '), ")") : '';
}
function hslToRgb(color) {
  var decomposedColor = decomposeColor(color);
  var values = decomposedColor.values;
  var h = values[0];
  var s = values[1] / 100;
  var l = values[2] / 100;
  var a = s * Math.min(l, 1 - l);
  var f = function f(n) {
    var k = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : (n + h / 30) % 12;
    return l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
  };
  var type = 'rgb';
  var rgb = [Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255)];
  if (decomposedColor.type === 'hsla') {
    type += 'a';
    values[3] && rgb.push(values[3]);
  }
  return recomposeColor({
    type: type,
    values: rgb
  });
}
function decomposeColor(color) {
  if (typeof color !== 'string') return color;
  if (color.charAt(0) === '#') {
    return decomposeColor(hexToRgb(color));
  }
  var marker = color.indexOf('(');
  var type = color.substring(0, marker);
  if (['rgb', 'rgba', 'hsl', 'hsla', 'color'].indexOf(type) === -1) {
    console.error('Resi-UI: Unsupported `%s` color.\n' + 'The following formats are supported: #nnn, #nnnnnn, rgb(), rgba(), hsl(), hsla(), color().', color);
  }
  var values;
  var colorSpace;
  var colorSubstring = color.substring(marker + 1, color.length - 1);
  if (type === 'color') {
    values = colorSubstring.split(' ');
    colorSpace = values.shift();
    if (values.length === 4 && values[3].charAt(0) === '/') {
      values[3] = values[3].substr(1);
    }
    if (colorSpace && ['srgb', 'display-p3', 'a98-rgb', 'prophoto-rgb', 'rec-2020'].indexOf(colorSpace) === -1) {
      console.error('Resi-UI: unsupported `%s` color space.\n' + 'The following color spaces are supported: srgb, display-p3, a98-rgb, prophoto-rgb, rec-2020.', colorSpace);
    }
  } else {
    values = colorSubstring.split(',');
  }
  values = values.map(function (value) {
    return parseFloat(value);
  });
  return {
    type: type,
    values: values,
    colorSpace: colorSpace
  };
}
function recomposeColor(color) {
  var colorSpace = color.colorSpace,
      type = color.type;
  var values = color.values;
  var recomposedColor;
  var newValues = _toConsumableArray(values);
  if (type.indexOf('rgb') !== -1) {
    newValues = values.map(function (n, i) {
      return i < 3 && typeof n === 'string' ? parseInt(n, 10) : n;
    });
  } else if (type.indexOf('hsl') !== -1) {
    newValues[1] = "".concat(values[1], "%");
    newValues[2] = "".concat(values[2], "%");
  }
  if (type.indexOf('color') !== -1) {
    recomposedColor = "".concat(colorSpace, " ").concat(newValues.join(' '));
  } else {
    recomposedColor = "".concat(newValues.join(', '));
  }
  return "".concat(type, "(").concat(recomposedColor, ")");
}
function getContrastRatio(foreground, background) {
  var lumA = getLuminance(foreground);
  var lumB = getLuminance(background);
  return (Math.max(lumA, lumB) + 0.05) / (Math.min(lumA, lumB) + 0.05);
}
function getLuminance(color) {
  var decomposedColor = decomposeColor(color);
  var rgb = decomposedColor.type === 'hsl' ? decomposeColor(hslToRgb(color)).values : decomposedColor.values;
  rgb = rgb.map(function (val) {
    var calculatedVal = val;
    if (decomposedColor.type !== 'color') {
      calculatedVal /= 255;
    }
    return calculatedVal <= 0.03928 ? calculatedVal / 12.92 : Math.pow((calculatedVal + 0.055) / 1.055, 2.4);
  });
  return Number((0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]).toFixed(3));
}
function darken(color, coefficient) {
  var decomposedColor = decomposeColor(color);
  var clampedCoefficient = clamp(coefficient);
  if (decomposedColor.type.indexOf('hsl') !== -1) {
    decomposedColor.values[2] *= 1 - clampedCoefficient;
  } else if (decomposedColor.type.indexOf('rgb') !== -1 || decomposedColor.type.indexOf('color') !== -1) {
    for (var i = 0; i < 3; i += 1) {
      decomposedColor.values[i] *= 1 - clampedCoefficient;
    }
  }
  return recomposeColor(decomposedColor);
}
function lighten(color, coefficient) {
  var decomposedColor = decomposeColor(color);
  var clampedCoefficient = clamp(coefficient);
  if (decomposedColor.type.indexOf('hsl') !== -1) {
    decomposedColor.values[2] += (100 - decomposedColor.values[2]) * clampedCoefficient;
  } else if (decomposedColor.type.indexOf('rgb') !== -1) {
    for (var i = 0; i < 3; i += 1) {
      decomposedColor.values[i] += (255 - decomposedColor.values[i]) * clampedCoefficient;
    }
  } else if (decomposedColor.type.indexOf('color') !== -1) {
    for (var _i = 0; _i < 3; _i += 1) {
      decomposedColor.values[_i] += (1 - decomposedColor.values[_i]) * clampedCoefficient;
    }
  }
  return recomposeColor(decomposedColor);
}

export { darken, decomposeColor, getContrastRatio, getLuminance, hexToRgb, hslToRgb, lighten, recomposeColor };
//# sourceMappingURL=colorManipulator.js.map
